import { Button } from '@techem/ui-components';
import option3Icon from 'presentation/assets/icons/svg/icn-abrechnung-mdl.svg';
import option1Icon from 'presentation/assets/icons/svg/icn-abrechnung-neu.svg';
import option2Icon from 'presentation/assets/icons/svg/icn-abrechnung-selbst.svg';
import { SIZE } from 'baseui/input';
import IconRadioGroup from 'presentation/components/IconRadioGroup';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardSteps, WizardStepsContainer } from 'presentation/components/WizardSteps';
import { DISABLE_FLOW_DEEP_LINKING_PROTECTION } from 'config';
import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { setHeatingCostsInvoice as setHeatingCostsInvoiceInStore } from 'frameworks_drivers/redux/reducers/onboardingInformation/onboardingInformationSlice';
import { paths } from 'frameworks_drivers/routes';
import store from 'store';
import useHotKey from 'tools/hooks/useHotKey';
import { RadioOptionKeys, translationKeyForRadioOption } from 'tools/RadioOptionMapper';
import { TrackerUtil } from 'tracking/TrackerUtil';

const ThermalMeteringPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prevPath = paths.ApartmentCount;

  useEffect(() => {
    if (
      !DISABLE_FLOW_DEEP_LINKING_PROTECTION &&
      // Assumption: previous page has to be filled with user input before the user is allowed to visit this page
      !store.getState().onboardingInfo.numberOfApartments
    ) {
      navigate(prevPath, { replace: true });
    }
  }, [prevPath, navigate]);

  const [heatingCostsInvoice, setHeatingCostsInvoice] = useState<string>(
    store.getState().onboardingInfo.heatingCostsInvoice
  );

  const radioData = [
    {
      icon: option1Icon,
      key: RadioOptionKeys.HeatingCostsInvoice.None,
      trackingId: 'radio-button_not-yet'
    },
    {
      icon: option2Icon,
      key: RadioOptionKeys.HeatingCostsInvoice.Personal,
      trackingId: 'radio-button_self-reading'
    },
    {
      icon: option3Icon,
      key: RadioOptionKeys.HeatingCostsInvoice.Professional,
      trackingId: 'radio-button_metering-service-provider'
    }
  ].map((opt) => ({
    icon: opt.icon,
    label: t(translationKeyForRadioOption(opt.key)),
    optionKey: opt.key,
    trackingId: opt.trackingId
  }));

  useHotKey((event) => {
    if (event.key === '1') setHeatingCostsInvoice(radioData[0].optionKey);
    if (event.key === '2') setHeatingCostsInvoice(radioData[1].optionKey);
    if (event.key === '3') setHeatingCostsInvoice(radioData[2].optionKey);
  });

  const handleSubmit = (e: React.FormEvent) => {
    store.dispatch(setHeatingCostsInvoiceInStore(heatingCostsInvoice));

    // Disable Providerchange in order of https://jira.techem-agile.de/browse/PKD-2877

    // if (
    //   heatingCostsInvoice !== RadioOptionKeys.HeatingCostsInvoice.Professional && false
    // ) {
    //   // Ensure provider transfer is unselected (could have been selected previously)
    //   // as it is not appropriate anymore.
    //   store.dispatch(
    //     setSelectedProducts(
    //       store.getState().onboardingInfo.selectedProducts.filter((product) => {
    //         return product.name !== Products.ProviderChangeService;
    //       })
    //     )
    //   );
    // }

    TrackerUtil.trackNextBtnClick();
    navigate(paths.CentralHeating);

    e.preventDefault();
  };

  const canBeSubmitted = () => {
    return heatingCostsInvoice !== '';
  };

  const handlePrevBtnClicked = () => {
    TrackerUtil.trackPrevBtnClick();
    navigate(prevPath);
  };

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer>
        <WizardSteps currentStep={2} totalSteps={6} />
      </WizardStepsContainer>
      <TitleWithPrevPageLink
        onPrevClick={handlePrevBtnClicked}
        title={t('pageHeatingCostsInvoiceTitle')}
      />

      <form onSubmit={handleSubmit}>
        <Row className="justify-content-center mt-4">
          <Col xs={12} md={10} lg={8}>
            <fieldset>
              <legend id="thermal-metering-label" className="visually-hidden">
                {t('pageThermalMeteringTitle')}
              </legend>
              <IconRadioGroup
                radioData={radioData}
                handleChange={(newVal: string) => setHeatingCostsInvoice(newVal)}
                currentSelection={() => heatingCostsInvoice}
                aria-labelledby="thermal-metering-label"
              />
            </fieldset>
          </Col>
        </Row>

        <Row className="justify-content-center py-5">
          <Col className="col-auto">
            <Button
              disabled={!canBeSubmitted()}
              size={SIZE.large}
              type="submit"
              additionalTestId="next"
            >
              {t('nextBtnText')}
            </Button>
          </Col>
        </Row>
      </form>
    </Container>
  );
};

export default ThermalMeteringPage;
